import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";
import { Input } from "@monorepo-apps/ui/src/components/input";

import { useAllProductScores } from "@/application/use-cases/products/use-scores.queries";
import { useAllFeatureProducts } from "@/application/use-cases/products/use-feature-products.queries";
import { useRemoveProduct } from "@/application/use-cases/products/use-products.queries";
import type { ProductRow } from "@/infrastructure/supabase/database.types";

interface DeleteProductDialogProps {
  product: ProductRow | null;
  onClose: () => void;
}

export function DeleteProductDialog({ product, onClose }: DeleteProductDialogProps) {
  return (
    <Dialog open={product !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="!max-w-lg">
        {product ? <Body product={product} onClose={onClose} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function Body({ product, onClose }: { product: ProductRow; onClose: () => void }) {
  const [typed, setTyped] = useState("");
  const scoresQ = useAllProductScores();
  const fpQ = useAllFeatureProducts();
  const remove = useRemoveProduct();
  const navigate = useNavigate();

  const scoreCount = (scoresQ.data ?? []).filter((s) => s.product_id === product.id).length;
  const fpCount = (fpQ.data ?? []).filter((f) => f.product_id === product.id).length;

  const canDelete = typed.trim() === product.name;

  const handleDelete = () => {
    if (!canDelete) return;
    remove.mutate(product.id, {
      onSuccess: () => {
        onClose();
        void navigate({ to: "/products" });
      },
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="size-4 text-[oklch(0.62_0.18_25)]" />
          Delete product
        </DialogTitle>
        <DialogDescription>This is irreversible.</DialogDescription>
      </DialogHeader>
      <div
        className="rounded-lg border p-3 text-[12.5px]"
        style={{ borderColor: "oklch(0.78 0.18 25 / 0.40)", background: "oklch(0.97 0.04 25)" }}
      >
        <p className="font-medium" style={{ color: "oklch(0.45 0.18 25)" }}>
          Deleting <strong>{product.name}</strong> will:
        </p>
        <ul className="mt-2 list-disc space-y-0.5 pl-5" style={{ color: "oklch(0.45 0.01 240)" }}>
          <li>
            Remove {scoreCount} product score row{scoreCount === 1 ? "" : "s"}
          </li>
          <li>
            Remove {fpCount} feature-coverage assignment{fpCount === 1 ? "" : "s"}
          </li>
          <li>
            Set product_id to NULL on any candidate choice referencing it (the slot becomes TBD)
          </li>
        </ul>
      </div>
      <div className="space-y-2">
        <label className="text-foreground/80 text-[12px]">
          Type <code className="font-data bg-muted/60 rounded px-1">{product.name}</code> to confirm
        </label>
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={product.name}
          autoComplete="off"
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          disabled={!canDelete || remove.isPending}
          onClick={handleDelete}
        >
          {remove.isPending ? "Deleting…" : "Delete product"}
        </Button>
      </DialogFooter>
    </>
  );
}
